
import 'three/examples/js/lines/LineSegments2';
import 'three/examples/js/lines/LineSegmentsGeometry';

import SBPBoardBoundingBox from './SBPBoardBoundingBox';
import SBPBoundingBox from './SBPBoundingBox';

import TextSprite from '../TextSprite';
import SBPBin from './SBPBin';
import SBPBinLine from './SBPBinLine';
import SBPCNC from './SBPCNC';
import SBPLine from './SBPLine';
import SBPScrew from './SBPScrew';
import { t } from '@/plugins/i18n';

const LineTypes = Object.freeze({
  JOG: Symbol('jog'),
  MOVE: Symbol('move'),
  CIRCLE: Symbol('circle'),
  POCKET: Symbol('pocket'),
});

const MoveColor = new THREE.Color(0x101010);

const Palettes = {
  [LineTypes.JOG]: {
    color: new THREE.Color(0xF6AD3C),
    alpha: 0.5
  },
  [LineTypes.MOVE]: {
    color: MoveColor,
    alpha: 1.0
  },
  [LineTypes.CIRCLE]: {
    color: MoveColor,
    alpha: 1.0
  },
  [LineTypes.POCKET]: {
    color: new THREE.Color(0xff7b7b),
    alpha: 1.0
  }
};

class Segment {
  constructor (start, end, type, isPoint = false) {
    this.start = start;
    this.end = end;
    this.length = end.distanceTo(start);
    this.type = type;
    this.isPoint = isPoint;
  }
}

export {
  LineTypes,
  Palettes
};

export default class SBPVisualizer extends THREE.Object3D {
  constructor (bin, fileName = 'cut_000.sbp', sheetLabel = {}) {
    super();

    this._t = 1.0;
    this._binVisibled = true;
    this._lineVisibled = true;
    this._tabVisibled = true;
    this._bisuVisibled = true;
    // nesting labelling
    this._labelinfoVisibled = true;

    this.boundingBox = null;

    // setup board
    this.boardBoundingBox = new SBPBoardBoundingBox(bin);
    this.add(this.boardBoundingBox);

    const offset = 20;
    // filename can be cut_AAA.sbp or cut_abc123.sbp
    const match = fileName.match(/cut_(.+)\.sbp/i);
    const index = match && match[1] ? match[1] : 'unknown';
    //const index = fileName.match(/\d/g).join('');
    this.numberOfPartsText = new TextSprite(`#${index}, Mill:${bin.millSize}mm, Parts:${bin.count}`, '#000000', 16);
    this.numberOfPartsText.position.set(
      this.boardBoundingBox.min.x - offset,
      this.boardBoundingBox.max.y + offset,
      this.boardBoundingBox.max.z + offset
    );
    this.add(this.numberOfPartsText);

    // Processing Time
    const cutTime = this.calculateCutTime(bin.cutTime);
    if (bin.cutTime > 0)
    {
      this.cutTimeText = new TextSprite(cutTime, '#000000', 12);
      this.cutTimeText.position.set(
        this.boardBoundingBox.max.x - (offset * 2),
        this.boardBoundingBox.min.y - (offset * 3),
        this.boardBoundingBox.max.z + offset
      );
      this.add(this.cutTimeText);
    }


    // nesting labelling
    this.labelinfo = new THREE.Group();
    this.add(this.labelinfo);

    if (sheetLabel && sheetLabel.label){
      this.sheetLabelText = new TextSprite(sheetLabel.label, '#000000', 16);
      this.sheetLabelText.position.set(
        this.boardBoundingBox.min.x + offset,
        this.boardBoundingBox.min.y - offset,
        this.boardBoundingBox.max.z + offset
      );
      //this.add(this.sheetLabelText)
      this.labelinfo.add(this.sheetLabelText)
    }

    this.lines = [];
    this.bins = [];

    this.cnc = new SBPCNC();
    this.add(this.cnc);

    this.tabs = new THREE.Group();
    this.add(this.tabs);

    //bisu
    this.bisus = new THREE.Group();
    this.add(this.bisus);

  }

  calculateCutTime(cutTime)
  {
    const hours = Math.floor(cutTime / 3600);
    const minutes = Math.floor((cutTime % 3600) / 60);
    const seconds = Math.round(cutTime % 60);

    return `${hours}${t('時間')} ${minutes}${t('分')} ${seconds}${t('秒')}`;
  }

  setup (options, sbpColor, labellingData) {
    const {
      renderer, bin, screwSegment, cutSegment, vbitSegment, texture, color, cncGeometry, cncOffset
    } = options;

    // タブ位置の表示
    const tabs = this.findTabs(cutSegment);
    this.addTabs(tabs, bin.thickness * 1.2);
    this.addBisu(screwSegment, bin.millSize, bin.thickness );

    this.addLabelinfo(labellingData);
    //this.addSheetLabelInfo(sheetLabellingData);

    if (sbpColor) { // change sbp color to designated color if it is needed
      Palettes[LineTypes.MOVE].color = sbpColor;
      Palettes[LineTypes.CIRCLE].color = sbpColor;
    }

    let segments = screwSegment.concat(cutSegment);
    if (vbitSegment) {
      segments = segments.concat(vbitSegment);
    }
    const attributes = this.buildAttributes(segments, bin.thickness);

    const lg = this.buildLineSegmentsGeometry(attributes);
    const sbpLine = new SBPLine(lg, 1);
    this.add(sbpLine);

    // mill size = 6.35mm or 12.7mm if height >= 30mm
    // const mill = ((bin.thickness >= 30) ? 12.7 : 6.35) + 0.25; // 0.25 is offset for pocket processings
    const mill = bin.millSize + 0.25; // 0.25 is offset for pocket processings
    const sbpBinLine = new SBPBinLine(lg, mill, bin.width, bin.depth);
    const sbpScrew = new SBPScrew(attributes, screwSegment, cutSegment, vbitSegment, mill);
    const sbpBin = new SBPBin(bin.width, bin.depth, bin.thickness, texture, color, bin.xoffset, bin.yoffset);

    sbpBin.position.copy(new THREE.Vector3(bin.width * 0.5, bin.depth * 0.5, bin.thickness * 0.5));

    sbpBin.setup(renderer, sbpBinLine, sbpScrew);
    sbpBin.visible = this.binVisibled;
    this.add(sbpBin);
    // this.add(sbpScrew) // debug

    this.lines.push(sbpLine);
    this.bins.push(sbpBin);

    this.cnc.setup({ attributes, bin, offset: cncOffset, cncGeometry });
    this.cnc.position.set(0, 0, bin.thickness);
  }

  applyTexture (texture) {
    this.bins.forEach((bin) => {
      bin.traverse((mesh) => {
        const { material } = mesh;
        if (material !== undefined) {
          const { map, normalMap } = texture;
          if (material.uniforms !== undefined) {
            const uniforms = mesh.material.uniforms;
            uniforms.map.value = map;
            uniforms.normalMap.value = normalMap;
          } else {
            material.map = map;
            material.normalMap = normalMap;
          }
        }
      });
    });
  }

  get t () {
    return this._t;
  }

  set t (v) {
    this._t = v;

    this.lines.forEach(line => {
      line.material.uniforms.t.value = v;
    });

    this.bins.forEach(bin => {
      bin.t = v;
    });

    this.cnc.t = v;
  }

  get binVisibled () {
    return this._binVisibled;
  }

  set binVisibled (v) {
    this.bins.forEach(bin => {
      bin.visible = v;
    });
  }

  get lineVisibled () {
    return this._lineVisibled;
  }

  set lineVisibled (v) {
    this.lines.forEach(line => {
      line.visible = v;
    });
  }

  get cncVisibled () {
    return this.cnc.visible;
  }

  set cncVisibled (v) {
    this.cnc.visible = v;
  }

  get tabVisibled() {
    return this._tabVisibled;
  }

  set tabVisibled(v) {
    this.tabs.visible = v
  }

  get bisuVisibled() {
    return this._bisuVisibled;
  }

  set bisuVisibled(v) {
    this.bisus.visible = v
  }

  set labelinfoVisibled(v) {
    this.labelinfo.visible = v
  }


  render (renderer) {
    if (this.binVisibled && this.prevT !== this.t) {
      this.bins.forEach(bin => bin.render(renderer));
    }
    this.prevT = this.t;
  }

  resize (w, h) {
    if (this.boundingBox !== null) { this.boundingBox.resize(w, h); }

    if (this.boardBoundingBox !== null) { this.boardBoundingBox.resize(w, h); }

    const baseHeight = 640;
    const ratio = baseHeight / h;
    const tScale = (new THREE.Vector2(1, 1)).multiplyScalar(ratio);
    this.numberOfPartsText.resize(tScale);

    if (this.sheetLabelText) {
      this.sheetLabelText.resize(tScale);
    }
  }

  parse (data, isScrew = false) {
    const rows = data.split('\n');
    let currentPosition = new THREE.Vector3(0, 0, 0);
    let segmentType = 'exterior';

    let segments = [];

    for (let i = 0, rowsCount = rows.length; i < rowsCount; i++) {
      let row = rows[i];

      if (row.includes('POCKET')) {
        segmentType = 'pocket';
      } 
      else if (row.includes('EXTERIOR')) {
        segmentType = 'exterior'; 
      }
      else if (row.includes('INTERIOR')) {
        segmentType = 'interior';
      }
      else if (row.includes('SINGLE')) {
        segmentType = 'single';
      }
      
      let cols = row.split(',');
      let start = currentPosition.clone();
      let end = new THREE.Vector3().copy(start);

      let lineType;
      let isPoint = false;
      switch (cols[0][0]) {
        case 'CP':
          lineType = LineTypes.CIRCLE;
          break;
        case 'J':
          lineType = LineTypes.JOG;
          isPoint = rows[i - 1].includes('POINT');
          break;
        case 'M':
          if (isScrew) {isPoint = true;}
          lineType = LineTypes.MOVE;
          if (segmentType === 'pocket')
          {
            lineType = LineTypes.POCKET;
          }
          break;
      }

      switch (cols[0][1]) {
        case '2':
          end.x = parseFloat(cols[1]);
          end.y = parseFloat(cols[2]);
          break;
        case '3':
          end.x = parseFloat(cols[1]);
          end.y = parseFloat(cols[2]);
          end.z = parseFloat(cols[3]);
          break;
        case 'X':
          end.x = parseFloat(cols[1]);
          break;
        case 'Y':
          end.y = parseFloat(cols[1]);
          break;
        case 'Z':
          end.z = parseFloat(cols[1]);
          break;
      }

      switch (lineType) {
        case LineTypes.JOG:
        case LineTypes.MOVE:
        case LineTypes.POCKET:
          currentPosition.copy(end);
          // segments.push(new Segment(start.clone().add(offset), end.clone().add(offset), lineType))
          segments.push(new Segment(start, end, lineType, isPoint));
          break;
        case LineTypes.CIRCLE:
          this.addCircle(segments, cols, end);
          break;
      }
    }

    return segments;
  }

  setupBoundingBox (segments) {
    let minmax = this.computeBoundingBox(segments);
    this.boundingBox = new SBPBoundingBox(minmax.min, minmax.max);
    this.add(this.boundingBox);
  }

  buildAttributes (segments, binThickness) {
    let positions = [];
    let uvs = [];
    let colors = [];
    let depth = [];

    let total = segments.reduce((cur, s) => {
      return cur + s.length;
    }, 0);
    let inv = 1.0 / total;

    let minZ = Number.MAX_VALUE;
    let maxZ = Number.MIN_VALUE;
    segments.forEach((s) => {
      let z0 = s.start.z;
      let z1 = s.end.z;
      minZ = Math.min(z0, minZ);
      minZ = Math.min(z1, minZ);
      maxZ = Math.max(z0, maxZ);
      maxZ = Math.max(z1, maxZ);
    });
    maxZ = Math.min(binThickness, maxZ)
    let lz = maxZ - minZ || 1;

    let offset = 0;
    segments.forEach((s, i) => {
      let t0 = offset;
      offset += s.length * inv;
      let t1 = offset;
      positions.push(s.start.x, s.start.y, s.start.z, s.end.x, s.end.y, s.end.z);
      uvs.push(t0, 0, t1, 0);

      const palette = Palettes[s.type];
      const color = palette.color;
      const alpha = palette.alpha;

      let z0 = (s.start.z - minZ) / lz;
      let z1 = (s.end.z - minZ) / lz;
      colors.push(color.r, color.g, color.b, alpha, color.r, color.g, color.b, alpha);
      depth.push(z0, z1);
    });

    return {
      positions, uvs, colors, depth
    };
  }

  buildLineSegmentsGeometry (attributes) {
    const { positions, uvs, colors, depth } = attributes;

    // let geometry = new THREE.BufferGeometry()
    // geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    // geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
    // geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

    const lg = new THREE.LineSegmentsGeometry();
    lg.setPositions(positions);

    {
      let instanceColorBuffer = new THREE.InstancedInterleavedBuffer(new Float32Array(colors), 8, 1); // rgba, rgba
      lg.setAttribute('instanceColorStart', new THREE.InterleavedBufferAttribute(instanceColorBuffer, 4, 0)); // rgba
      lg.setAttribute('instanceColorEnd', new THREE.InterleavedBufferAttribute(instanceColorBuffer, 4, 4)); // rgba
    }

    {
      let instanceUVBuffer = new THREE.InstancedInterleavedBuffer(new Float32Array(uvs), 4, 1); // uv, uv
      lg.setAttribute('instanceUVStart', new THREE.InterleavedBufferAttribute(instanceUVBuffer, 2, 0)); // uv
      lg.setAttribute('instanceUVEnd', new THREE.InterleavedBufferAttribute(instanceUVBuffer, 2, 2)); // uv
    }

    {
      let instanceDepthBuffer = new THREE.InstancedInterleavedBuffer(new Float32Array(depth), 2, 1);
      lg.setAttribute('instanceDepthStart', new THREE.InterleavedBufferAttribute(instanceDepthBuffer, 1, 0)); // rgba
      lg.setAttribute('instanceDepthEnd', new THREE.InterleavedBufferAttribute(instanceDepthBuffer, 1, 1)); // rgba
    }

    return lg;
  }

  addCircle (segments, cols, end) {
    let diameter = parseFloat(cols[1]);
    let centerX = parseFloat(cols[2]);
    let centerY = parseFloat(cols[3]);
    let plunge = parseFloat(cols[8]);
    let repetitions = parseInt(cols[9]);

    let radius = diameter / 2;
    const resolution = 8;

    let points = [];

    for (let i = 0; i <= resolution; i++) {
      let v = new THREE.Vector3(
        radius * Math.cos(2 * Math.PI * i / resolution) + centerX,
        radius * Math.sin(2 * Math.PI * i / resolution) + centerY,
        end.z
      );
      points.push(v);
    }

    for (let i = 1; i <= repetitions; i++) {
      for (let j = 0; j < resolution; j++) {
        let p0 = points[j].clone();
        let p1 = points[j + 1].clone();
        p0.z = p1.z = end.z + plunge * i;
        segments.push(new Segment(p0, p1, LineTypes.CIRCLE));
      }
    }
  }

  computeBoundingBox (segments) {
    let min = new THREE.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let max = new THREE.Vector3(Number.MIN_VALUE, Number.MIN_VALUE, Number.MIN_VALUE);

    segments.forEach(s => {
      min.min(s.start);
      min.min(s.end);
      max.max(s.start);
      max.max(s.end);
    });

    return {
      min: min,
      max: max
    };
  }

  findTabs(segments) {
    let tabs = [];

    for (let i = 0; i < segments.length - 2; i++) {
      let currentSegment = segments[i];
      let nextSegment = segments[i + 1];
      let nextNextSegment = segments[i + 2];

      if (
        currentSegment.type !== LineTypes.MOVE ||
        nextSegment.type !== LineTypes.MOVE ||
        nextNextSegment.type !== LineTypes.MOVE
      ) continue;

      let isZIncrease = (currentSegment.end.z - currentSegment.start.z) > 0;
      let isParallelMove =
        (nextSegment.end.x !== nextSegment.start.x) ||
        (nextSegment.end.y !== nextSegment.start.y);
      let isZDecrease = (nextNextSegment.end.z - nextNextSegment.start.z) < 0;

      if (isZIncrease && isParallelMove && isZDecrease) {
        let midpoint = currentSegment.start.clone().lerp(nextNextSegment.end, 0.5);
        midpoint.z = 0;
        tabs.push(midpoint);
        i += 2;  // タブを検出したら、次の2つのsegmentsはすでに確認済みなのでスキップ
      }
    }

    function isNearby(a, b) {
      return a.distanceTo(b) < 10;
    }

    function filterNearbyTabs(tabPositions) {
      let uniqueTabs = [];

      while (tabPositions.length > 0) {
        let currentTab = tabPositions.pop();
        uniqueTabs.push(currentTab);

        // 現在のタブに近いタブの位置をリストから削除
        tabPositions = tabPositions.filter(tab => !isNearby(currentTab, tab));
      }

      return uniqueTabs;
    }

    tabs = filterNearbyTabs(tabs);
    return tabs;
  }

  addTabs(tabs, size) {
    const geometry = new THREE.SphereGeometry(size, 8, 8);
    const material = new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.3 });
    tabs.forEach(tab => {
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.copy(tab);
      this.tabs.add(sphere);
    });
  }


  addBisu(screwSegements, size, thickness)
  {
    let screws = [];
    for (let i = 1; i < screwSegements.length - 1; i++) {
      if (screwSegements[i].isPoint === false) continue;
      //console.log('screw point', screwSegements[i].end);
      screws.push(screwSegements[i].end);
    }

    const geometry = new THREE.CircleGeometry(size/2, 32);
    const material = new THREE.MeshBasicMaterial({ color: 0x0000ff, transparent: true, opacity: 0.3, side: THREE.DoubleSide });
    screws.slice(1).forEach(screw => {
      const sphere = new THREE.Mesh(geometry, material);
      const pos = screw.clone();
      pos.Z = thickness;
      sphere.position.copy(pos);
      this.bisus.add(sphere);
    });
  }

  addLabelinfo(labellingData){
    if (!labellingData) return
    for(let data of labellingData) {

      let position = data.position;
      let layer_txt = ''
      let splitText = data.label.split(",")
      let rgbColorString = '#000000'
      if (splitText.length > 1) {
        let color_txt = data.label.split(",")[1].trim();
        if (color_txt.length>8) {
          rgbColorString = '#' + color_txt.slice(3,9);
        }
      }
      layer_txt = data.label.split(",")[0].trim();

      const label_point = new THREE.Vector3(position.x, position.y, 0)
      const layer_label_point = new THREE.Vector3(position.x, position.y -20, 0)
      let labelsText = new TextSprite("", rgbColorString, 10);
      if(data.name != null){
        labelsText = new TextSprite(`${data.name}`, rgbColorString, 10);
      }

      let layerLabelsText = new TextSprite(`${layer_txt}`, rgbColorString, 10);

      labelsText.position.copy(label_point);
      layerLabelsText.position.copy(layer_label_point);

      this.labelinfo.add(labelsText);
      this.labelinfo.add(layerLabelsText);
    }
  }

  addSheetLabelInfo(sheetLabellingData) {

  }

}



// WEBPACK FOOTER //
// ./src/assets/javascripts/sbp/SBPVisualizer.js